<template>
  <div class="verification-info">
    <template v-if="isOnboardingRedesign">
      <h2 class="question-title with-subtext mt-2">
        Help us verify your identity so a doctor can legally prescribe you medication.
      </h2>
      <p
        class="sub-text mb-2 tw-text-[#787878]"
      >
        Est. 2 minutes
      </p>
      <div class="mt-5">
        <h2>Please provide us with these items:</h2>
        <ol class="tw-list-decimal tw-pl-5 mb-4 tw-text-2xl tw-space-y-4">
          <li>A photo of your ID (required by your state to verify identity)</li>
          <li>A photo of your face (used to confirm the ID is yours)</li>
        </ol>
      </div>

      <div class="tw-text-center tw-mb-8">
        <a
          class="tw-underline tw-text-[#1A6A72] tw-cursor-pointer"
          @click.prevent="showVerifyIdentityExplanation = true"
        >
          Why do you need to verify my identity?
        </a>
      </div>
    </template>
    <template v-else>
      <h2 class="question-title with-subtext mt-2">
        Ok great! The Winona physician is ready to review your information.
      </h2>
      <p class="sub-text mb-3 tw-leading-none">
        You're almost done. In the next step, we'll verify your identity so your
        doctor can legally write you a prescription.
      </p>
      <p
        class="sub-text mb-2"
        style="color: #a783ff"
      >
        2 minute task
      </p>
      <div class="mt-5">
        <h2>Here's what's needed:</h2>
        <h2 class="med-font mb-4 row align-items-center">
          <b-col cols="auto">
            <b-avatar
              variant="primary"
              text="1"
              size="1.5rem"
            />
          </b-col>
          <b-col>
            A photo of your ID (required by your state to verify identity)
          </b-col>
        </h2>
        <h2 class="med-font row align-items-center">
          <b-col cols="auto">
            <b-avatar
              variant="primary"
              text="2"
              size="1.5rem"
            />
          </b-col>
          <b-col> A photo of your face (used to confirm the ID is yours) </b-col>
        </h2>
      </div>

      <p class="sub-text my-5 tw-mb-[130px] sm:tw-mb-0">
        A government-issued ID is required when purchasing any prescription
        medication, as the medical provider will need to verify your identity
        before they can determine if prescription medication is right for you.
        Your personal identifying information, medical history or ID will never
        be shared outside of our secure system.
      </p>
    </template>

    <div class="tw-text-center tw-text-[#848484]">
      <OnboardingTLSSecurity />
    </div>

    <OnboardingCTAContainer>
      <div class="tw-flex tw-flex-col tw-w-full tw-gap-2">
        <b-button
          class="slight-rounded tw-flex tw-gap-2 tw-items-center tw-justify-center"
          :class="{'tw-max-w-[300px] tw-mx-auto tw-text-base md:tw-text-lg': isOnboardingRedesign}"
          variant="info"
          pill
          block
          @click="next"
        >
          <svg
            width="16"
            height="19"
            viewBox="0 0 16 19"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M14.8182 8.42H13.3693V3.38C13.3693 1.78925 12.1484 0.5 10.642 0.5H5.35795C3.85156 0.5 2.63068 1.78925 2.63068 3.38V8.42H1.18182C0.804688 8.42 0.5 8.74175 0.5 9.14V17.78C0.5 18.1783 0.804688 18.5 1.18182 18.5H14.8182C15.1953 18.5 15.5 18.1783 15.5 17.78V9.14C15.5 8.74175 15.1953 8.42 14.8182 8.42ZM4.16477 3.38C4.16477 2.68475 4.69957 2.12 5.35795 2.12H10.642C11.3004 2.12 11.8352 2.68475 11.8352 3.38V8.42H4.16477V3.38ZM13.9659 16.88H2.03409V10.04H13.9659V16.88ZM7.40341 13.7525V14.945C7.40341 15.044 7.48011 15.125 7.57386 15.125H8.42614C8.51989 15.125 8.59659 15.044 8.59659 14.945V13.7525C8.77247 13.6192 8.90375 13.4303 8.97153 13.2132C9.03931 12.996 9.04009 12.7618 8.97376 12.5441C8.90744 12.3265 8.77742 12.1366 8.60244 12.002C8.42745 11.8673 8.21652 11.7948 8 11.7948C7.78348 11.7948 7.57255 11.8673 7.39756 12.002C7.22258 12.1366 7.09256 12.3265 7.02624 12.5441C6.95991 12.7618 6.96069 12.996 7.02847 13.2132C7.09625 13.4303 7.22753 13.6192 7.40341 13.7525Z"
              fill="white"
            />
          </svg>
          Continue
        </b-button>
      </div>
    </OnboardingCTAContainer>

    <SideModal
      :show="showWhyDialog"
      body-class="why-body"
      @exit="hideWhyDialog()"
    >
      <template #header>
        <h1 class="h2 mt-6 mb-0 heading text-primary">
          Onboarding
        </h1>
      </template>
      <template #body>
        <b-row>
          <b-col>
            <b-row class="mt-5">
              <b-col><h3>Identity Verification</h3></b-col>
            </b-row>
            <b-row>
              <b-col
                cols="9"
                class="d-flex flex-column justify-content-between"
              >
                <span
                  class="text-small text-muted"
                >Why we need this information?</span>
                <p>
                  Government issued ID is required when purchasing any
                  prescription medication, as the medical provider will need to
                  verify your identity before they can determine if prescription
                  medication is right for you. Your personal identifying
                  information, medical history or ID will never be shared
                  outside of our secure system.
                </p>
              </b-col>
            </b-row>
          </b-col>
        </b-row>

        <b-row>
          <b-col
            cols="12"
            class="align-self-center text-right"
          >
            <b-button
              pill
              variant="info"
              style="min-width: 250px"
              @click="hideWhyDialog()"
            >
              OK
            </b-button>
          </b-col>
        </b-row>
      </template>
    </SideModal>

    <b-modal
      id="verify-identity-explanation"
      v-model="showVerifyIdentityExplanation"
      hide-header
      hide-footer
      centered
      content-class="tw-bg-white"
    >
      <div class="modal-card">
        <p
          class="sub-text tw-mb-8"
        >
          A medical provider is required to verify your identity with a government-issued ID before prescribing medication. Your personal identifiable information, medical history, and ID will remain confidential within our secure platform.
        </p>

        <div class="tw-flex tw-flex-col tw-gap-2">
          <b-button
            variant="info"
            pill
            style="min-width: 100px; width: 100%"
            class="slight-rounded"
            @click="next"
          >
            Accept and Continue
          </b-button>
        </div>
      </div>
    </b-modal>
    <div class="bg-overlay"></div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
// services
import { OnboardingService } from '@/services/onboarding.service';

// components
import OnboardingCTAContainer from '@/components/onboarding/OnboardingCTAContainer';
import SideModal from '@/components/partials/SideModal';
import OnboardingTLSSecurity from '@/components/onboarding/OnboardingTLSSecurity'

// mixins
import analyticsMixin from '@/mixins/analytics';

export default {
  components: {
    SideModal,
    OnboardingCTAContainer,
    OnboardingTLSSecurity
  },
  mixins: [analyticsMixin],
  props: {
    onboardingflow: {
      type: String,
      default: 'flowA',
    },
    analyticsId: {
      type: String,
      default: '',
    },
    state: {
      type: String,
      default: '',
    },
    phoneno: {
      type: String,
      default: '',
    },
    email: {
      type: String,
      default: '',
    },
    firstname: {
      type: String,
      default: '',
    },
    lastname: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      showWhyDialog: false,
      showVerifyIdentityExplanation: false
    };
  },
  computed: {
    ...mapState('global', ['isOnboardingRedesign']),
  },
  async mounted() {
    const onboarding = await OnboardingService.getPatientOnboarding();

    try {
      const eventCheckoutStarted = await OnboardingService.triggerOnboardingEvents({
        eventname: 'VerificationStarted',
        path: window.location.pathname,
      })
      if (eventCheckoutStarted.data.status === 'new') {
        await this.$_analytics_track(
          'Identity Verification Started',
          {
            customerId: this.analyticsId,
            onboardingflow: this.onboardingflow,
            state: this.state,
            phoneno: this.phoneno,
            email: this.email,
            messageID: eventCheckoutStarted.data.id,
            firstname: this.firstname,
            lastname: this.lastname,
          },
        );

        if (eventCheckoutStarted.data.id) {
          this.fbqTrack(onboarding.data, eventCheckoutStarted.data.id);
        } else if (
          eventCheckoutStarted.data.id &&
            process.env.VUE_APP_SERVER != 'production'
        ) {
          console.log('FB TRACKING VerificationStarted');
          console.log(
            'FB TRACKING VerificationStarted: ' +
                eventCheckoutStarted.data.id
          );
        }
      }
    } catch (err) {
      console.log(err);
    }

    await this.$_analytics_track(
      'Onboarding Screen Viewed',
      {
        customerId: this.analyticsId,
        screen: '4.1-A',
        onboardingflow: this.onboardingflow,
        firstname: this.firstname,
        lastname: this.lastname,
      },
    );
  },
  methods: {
    hideWhyDialog() {
      this.elementTag(null, false);
    },
    elementTag(style, Boolean) {
      this.showWhyDialog = Boolean;
      const htmlTag = document.documentElement;
      htmlTag.style.overflow = style;
      htmlTag.scrollTop = htmlTag.scrollHeight;
    },
    next() {
      this.showVerifyIdentityExplanation = false;
      this.$emit('setStep', 'idphoto');
    },
    async fbqTrack(data, eventID) {
      // FB Pixel Tracking
      try {
        fbq('init', '348219289912097', {
          em: data.email,
          fn: data.firstname,
          ln: data.lastname,
          ph: data.phoneno,
          external_id: data.id,
          extern_id: data.id,
        });
        // fbq("track", "PageView");
        const pageViewBody = { userid: data.id };
        OnboardingService.onboardingEvent('PageView', pageViewBody);
        fbq(
          'trackCustom',
          'VerificationStarted',
          {
            external_id: data.id,
            extern_id: data.id,
            state: this.state,
            ...(await this.$store.dispatch('getFBQParams')),
          },
          {
            eventID: eventID,
          }
        );
      } catch (err) {
        console.log(err);
      }
    },
  },
};
</script>
